import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Loader2, ArrowRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import LoginBrandPanel from '@/components/auth/LoginBrandPanel';
import EchoHireLogo from '@/components/branding/EchoHireLogo';
import ThemeToggle from '@/components/ThemeToggle';
import { cn } from '@/lib/utils';

/* ── Inline SVG microphone (matches brand panel) ── */
function MicIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M7 4a3 3 0 016 0v6a3 3 0 11-6 0V4z" />
      <path d="M5.5 9.643a.75.75 0 00-1.5 0V10a6 6 0 0011.996.47A.75.75 0 0014.5 10v-.357a.75.75 0 00-1.5 0V10a4.5 4.5 0 01-9 0v-.357z" />
    </svg>
  );
}

const LoginPage = ({ onLoginSuccess }) => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [focused, setFocused] = useState(null);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email?.trim() || !form.password) {
      setError('Please enter both your email and password.');
      return;
    }
    setIsLoading(true);
    try {
      await onLoginSuccess(form);
    } catch (err) {
      setError(err.message || 'Invalid email or password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-dvh bg-background">
      <style>{`
        @keyframes form-enter {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .form-enter { animation: form-enter 0.5s cubic-bezier(0.16,1,0.3,1) both; }

        .sign-in-btn {
          background: linear-gradient(135deg, #007a7a 0%, #009999 45%, #00b3b3 100%);
          transition: opacity 0.2s, transform 0.15s, box-shadow 0.2s;
        }
        .sign-in-btn:hover:not(:disabled) {
          opacity: 0.92;
          transform: translateY(-1px);
          box-shadow: 0 8px 32px rgba(0,153,153,0.32);
        }
        .sign-in-btn:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: none;
        }
        .sign-in-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        .input-wrap {
          transition: box-shadow 0.2s;
        }
        .input-wrap[data-focused='true'] {
          box-shadow: 0 0 0 3px rgba(0,153,153,0.18);
        }
      `}</style>

      {/* Theme toggle */}
      <div className="absolute right-4 top-4 z-30">
        <ThemeToggle size="small" />
      </div>

      <div className="relative grid min-h-dvh lg:grid-cols-[1.05fr_1fr]">

        {/* ── Left: Brand panel ── */}
        <LoginBrandPanel className="relative z-0 hidden lg:flex lg:flex-col" />

        {/* Seam blend */}
        <div className="login-split-fade pointer-events-none absolute inset-y-0 left-1/2 z-[5] hidden w-[min(10rem,38vw)] -translate-x-1/2 lg:block" aria-hidden />

        {/* ── Right: Form panel ── */}
        <div className={cn(
          'relative z-10 flex flex-col items-center justify-center px-6 py-14 sm:px-10 lg:min-h-screen lg:px-12 xl:px-16',
          'bg-background lg:bg-card dark:lg:bg-[#080f18]',
        )}>

          {/* Subtle top-edge accent line */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/20 to-transparent lg:hidden" aria-hidden />

          <div className="form-enter mx-auto w-full max-w-[21rem] sm:max-w-[22rem]">

            {/* Logo */}
            <div className="mb-10">
              <EchoHireLogo size="prominent" collapsed={false} />
            </div>

            {/* Heading */}
            <div className="mb-8">
              <h1 className="text-2xl font-bold tracking-tight text-foreground">
                Sign in to your workspace
              </h1>
              <p className="mt-1.5 text-sm text-foreground/50">
                Continue managing interviews, pipelines, and AI reports.
              </p>
            </div>

            {/* Error banner */}
            {error && (
              <div
                role="alert"
                className="mb-6 flex items-start gap-2.5 rounded-xl border border-destructive/30 bg-destructive/7 px-4 py-3 text-sm text-destructive"
              >
                <span className="mt-px shrink-0">⚠</span>
                <span>{error}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>

              {/* Email */}
              <div className="space-y-1.5">
                <Label
                  htmlFor="email"
                  className={cn(
                    'block text-xs font-semibold transition-colors duration-150',
                    focused === 'email' ? 'text-brand-500 dark:text-brand-400' : 'text-foreground/65',
                  )}
                >
                  Work email
                </Label>
                <div
                  className="input-wrap rounded-xl"
                  data-focused={focused === 'email' ? 'true' : 'false'}
                >
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@company.com"
                    value={form.email}
                    onChange={handleChange}
                    onFocus={() => setFocused('email')}
                    onBlur={() => setFocused(null)}
                    disabled={isLoading}
                    className={cn(
                      'h-11 rounded-xl border bg-muted/30 px-4 text-sm text-foreground',
                      'placeholder:text-foreground/30 transition-colors duration-150',
                      'dark:bg-white/5',
                      focused === 'email'
                        ? 'border-brand-500/60 dark:border-brand-400/50'
                        : 'border-border hover:border-border/80',
                    )}
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <Label
                  htmlFor="password"
                  className={cn(
                    'block text-xs font-semibold transition-colors duration-150',
                    focused === 'password' ? 'text-brand-500 dark:text-brand-400' : 'text-foreground/65',
                  )}
                >
                  Password
                </Label>
                <div
                  className="input-wrap relative rounded-xl"
                  data-focused={focused === 'password' ? 'true' : 'false'}
                >
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={form.password}
                    onChange={handleChange}
                    onFocus={() => setFocused('password')}
                    onBlur={() => setFocused(null)}
                    disabled={isLoading}
                    className={cn(
                      'h-11 rounded-xl border bg-muted/30 py-2 pr-11 pl-4 text-sm text-foreground',
                      'placeholder:text-foreground/30 transition-colors duration-150',
                      'dark:bg-white/5',
                      focused === 'password'
                        ? 'border-brand-500/60 dark:border-brand-400/50'
                        : 'border-border hover:border-border/80',
                    )}
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center rounded-xl text-foreground/35 transition-colors hover:text-foreground/70"
                    onClick={() => setShowPassword((v) => !v)}
                    disabled={isLoading}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className="sign-in-btn mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-xl text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  <>
                    Sign in
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-8 space-y-3 text-center">
              <p className="text-sm text-foreground/45">
                Don't have access?{' '}
                <a href="mailto:contact@echohire.in" className="font-semibold text-foreground/70 hover:underline">Ask your EchoHire admin</a>
              </p>
              <p className="text-[11px] text-foreground/30">
                By signing in you agree to use EchoHire for authorised hiring activities only.{' '}
                <a href="https://echohire.in" target="_blank" rel="noopener noreferrer" className="text-brand-500 hover:underline dark:text-brand-400">Home ↗</a>
              </p>
            </div>

            {/* Mobile brand strip */}
            <div className="mt-10 border-t border-border pt-6 lg:hidden">
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center gap-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-brand-500 text-white">
                    <MicIcon />
                  </div>
                  <span className="text-sm font-bold text-foreground">EchoHire</span>
                </div>
                <p className="text-center text-xs text-foreground/40">
                  AI-powered structured interviews · Detailed candidate reports · Team pipeline
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
